/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable no-useless-escape */
import React, { useState } from 'react';
import { FullScreenLoading, LargeButton, PassInput, RouterLink } from 'pg-ui-library';
import { useTranslation } from 'react-i18next';
import logoSvg from 'src/assets/logo_pride.svg';
import background from 'src/assets/changePasswordBg.png';
import { ErrorMessage, Form, Formik } from 'formik';
import * as yup from 'yup';
import { IResetPassword, IPage } from 'src/interfaces';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import resetPasswordService from 'src/services/reset-password.service';
import {
  BackgroundImage,
  Container,
  FormContainer,
  FooterContainer,
  FooterText,
  MenuContainer,
  FormTitle,
  ImageContainer,
  LinkContainer,
  Logo,
  GrayText,
  RedText,
  SubmitButtonContainer,
} from './styles';

const ChangePassword: React.FC<IPage> = () => {
  const currentYear = new Date().getFullYear();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as any;
  const from = state?.from?.pathname || '/';
  const [loading, setLoading] = useState(false);
  const validationSchema = yup.object({
    password: yup
      .string()
      .required(t('global.form.required'))
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/),
    confirmPassword: yup.string().oneOf([yup.ref('password'), null], t('signUp.form.passwordRestrictions.item06')),
  });
  const [changePasswordError, setChangePasswordError] = useState(false);

  const submit = async (values: IResetPassword) => {
    try {
      setLoading(true);
      const authToken = searchParams.get('token') || '';
      await resetPasswordService.resetPassword(values, authToken);
      setChangePasswordError(false);
      navigate(from, { replace: true });
    } catch (err: any) {
      setChangePasswordError(true);
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      {loading && <FullScreenLoading />}
      <Container>
        <MenuContainer>
          <FormContainer>
            <Logo src={logoSvg} alt="Pride Global logo" />
            <FormTitle>{t('changePassword.form.title')}</FormTitle>
            <Formik
              initialValues={{
                password: '',
                confirmPassword: '',
              }}
              onSubmit={submit}
              validationSchema={validationSchema}
            >
              {({ handleChange, isSubmitting, handleSubmit }) => (
                <Form onSubmit={handleSubmit}>
                  <PassInput placeholder={t('changePassword.form.password')} handleChange={handleChange} />
                  <PassInput
                    placeholder={t('changePassword.form.confirmPassword')}
                    name="confirmPassword"
                    id="confirmPassword"
                    handleChange={handleChange}
                  />
                  <ErrorMessage
                    data-cy="change-password-error"
                    name="password"
                    component={() => (
                      <>
                        <GrayText>{t('signUp.form.passwordRestrictions.title')}</GrayText>
                        <RedText>{t('signUp.form.passwordRestrictions.item01')}</RedText>
                        <RedText>{t('signUp.form.passwordRestrictions.item02')}</RedText>
                        <RedText>{t('signUp.form.passwordRestrictions.item03')}</RedText>
                        <RedText>{t('signUp.form.passwordRestrictions.item04')}</RedText>
                        <RedText>{t('signUp.form.passwordRestrictions.item05')}</RedText>
                      </>
                    )}
                  />
                  <ErrorMessage
                    data-cy="change-password-confirm-password-error"
                    name="confirmPassword"
                    component={RedText}
                  />
                  <SubmitButtonContainer>
                    <LargeButton type="submit" disabled={isSubmitting}>
                      {t('changePassword.form.button')}
                    </LargeButton>
                  </SubmitButtonContainer>
                </Form>
              )}
            </Formik>
            {changePasswordError && <RedText>{t('changePassword.form.serverError')}</RedText>}
          </FormContainer>
          <FooterContainer>
            <FooterText>{`PRIDE GLOBAL ${currentYear} ALL RIGHTS RESERVED`}</FooterText>
            <LinkContainer>
              <RouterLink to="/">{t('signUp.footer.termsOfUse')}</RouterLink>
              <RouterLink to="/">{t('signUp.footer.privacyPolicy')}</RouterLink>
            </LinkContainer>
          </FooterContainer>
        </MenuContainer>
        <ImageContainer>
          <BackgroundImage src={background} />
        </ImageContainer>
      </Container>
    </>
  );
};

export default ChangePassword;
